import { ref, uploadBytes, getDownloadURL, deleteObject, listAll, getMetadata } from 'firebase/storage';
import { storage } from '../firebase';
import { addDoc, collection, query, where, getDocs, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export interface FileMetadata {
  id: string;
  name: string;
  originalName: string;
  size: number;
  type: string;
  url: string;
  uploadedBy: string;
  schoolId: string;
  category: 'message' | 'task' | 'document' | 'backup' | 'profile' | 'other';
  parentId?: string; // ID of message, task, etc.
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  tags?: string[];
  thumbnailUrl?: string;
  virusScanStatus?: 'pending' | 'clean' | 'infected' | 'error';
}

export interface UploadProgress {
  fileName: string;
  bytesTransferred: number;
  totalBytes: number;
  percentage: number;
  isComplete: boolean;
  error?: string;
}

export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export class FileService {
  private static readonly MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
  private static readonly MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB per upload
  private static readonly ALLOWED_TYPES = [
    // Images
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
    // Documents
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'text/csv',
    // Archives
    'application/zip',
    'application/x-rar-compressed',
    // Audio
    'audio/mpeg', 'audio/wav', 'audio/ogg',
    // Video
    'video/mp4', 'video/webm', 'video/ogg'
  ];

  // Upload single file
  static async uploadFile(
    file: File,
    category: FileMetadata['category'],
    schoolId: string,
    uploadedBy: string,
    parentId?: string,
    isPublic = false,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<FileMetadata> {
    // Validate file
    const validation = this.validateFile(file);
    if (!validation.isValid) {
      throw new Error(`File validation failed: ${validation.errors.join(', ')}`);
    }

    const fileId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fileName = `${fileId}_${file.name}`;
    const storagePath = `${schoolId}/${category}/${fileName}`;
    
    try {
      // Create storage reference
      const storageRef = ref(storage, storagePath);

      // Upload file with progress tracking
      const uploadTask = uploadBytes(storageRef, file);
      
      if (onProgress) {
        onProgress({
          fileName: file.name,
          bytesTransferred: 0,
          totalBytes: file.size,
          percentage: 0,
          isComplete: false
        });
      }

      await uploadTask;

      if (onProgress) {
        onProgress({
          fileName: file.name,
          bytesTransferred: file.size,
          totalBytes: file.size,
          percentage: 100,
          isComplete: true
        });
      }

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Generate thumbnail for images
      let thumbnailUrl: string | undefined;
      if (file.type.startsWith('image/')) {
        thumbnailUrl = await this.generateThumbnail(file, storagePath + '_thumb');
      }

      // Save metadata to Firestore
      const metadata: Omit<FileMetadata, 'id'> = {
        name: fileName,
        originalName: file.name,
        size: file.size,
        type: file.type,
        url: downloadURL,
        uploadedBy,
        schoolId,
        category,
        parentId,
        isPublic,
        thumbnailUrl,
        virusScanStatus: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'files'), {
        ...metadata,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Start virus scan (async)
      this.startVirusScan(docRef.id, downloadURL);

      return { id: docRef.id, ...metadata };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw new Error(`Failed to upload ${file.name}: ${error.message}`);
    }
  }

  // Upload multiple files
  static async uploadFiles(
    files: File[],
    category: FileMetadata['category'],
    schoolId: string,
    uploadedBy: string,
    parentId?: string,
    isPublic = false,
    onProgress?: (progress: UploadProgress[]) => void
  ): Promise<FileMetadata[]> {
    // Validate total size
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > this.MAX_TOTAL_SIZE) {
      throw new Error(`Total file size (${this.formatFileSize(totalSize)}) exceeds limit (${this.formatFileSize(this.MAX_TOTAL_SIZE)})`);
    }

    const uploadPromises = files.map(async (file, index) => {
      const fileProgress: UploadProgress[] = new Array(files.length).fill(null).map((_, i) => ({
        fileName: files[i].name,
        bytesTransferred: 0,
        totalBytes: files[i].size,
        percentage: 0,
        isComplete: false
      }));

      return this.uploadFile(
        file,
        category,
        schoolId,
        uploadedBy,
        parentId,
        isPublic,
        (progress) => {
          fileProgress[index] = progress;
          onProgress?.(fileProgress);
        }
      );
    });

    return Promise.all(uploadPromises);
  }

  // Delete file
  static async deleteFile(fileId: string): Promise<void> {
    try {
      // Get file metadata
      const fileMetadata = await this.getFileMetadata(fileId);
      if (!fileMetadata) {
        throw new Error('File not found');
      }

      // Delete from storage
      const storageRef = ref(storage, fileMetadata.url);
      await deleteObject(storageRef);

      // Delete thumbnail if exists
      if (fileMetadata.thumbnailUrl) {
        const thumbRef = ref(storage, fileMetadata.thumbnailUrl);
        try {
          await deleteObject(thumbRef);
        } catch (error) {
          console.warn('Failed to delete thumbnail:', error);
        }
      }

      // Delete metadata from Firestore
      await updateDoc(doc(db, 'files', fileId), {
        deleted: true,
        deletedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deleting file:', error);
      throw new Error(`Failed to delete file: ${error.message}`);
    }
  }

  // Get file metadata
  static async getFileMetadata(fileId: string): Promise<FileMetadata | null> {
    try {
      const q = query(collection(db, 'files'), where('id', '==', fileId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) return null;
      
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() } as FileMetadata;
    } catch (error) {
      console.error('Error getting file metadata:', error);
      return null;
    }
  }

  // Get files by category and parent
  static async getFiles(
    schoolId: string,
    category?: FileMetadata['category'],
    parentId?: string,
    uploadedBy?: string
  ): Promise<FileMetadata[]> {
    try {
      let q = query(
        collection(db, 'files'),
        where('schoolId', '==', schoolId),
        where('deleted', '!=', true)
      );

      if (category) {
        q = query(q, where('category', '==', category));
      }

      if (parentId) {
        q = query(q, where('parentId', '==', parentId));
      }

      if (uploadedBy) {
        q = query(q, where('uploadedBy', '==', uploadedBy));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FileMetadata[];
    } catch (error) {
      console.error('Error getting files:', error);
      return [];
    }
  }

  // Validate file
  static validateFile(file: File): FileValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      errors.push(`File size (${this.formatFileSize(file.size)}) exceeds maximum allowed size (${this.formatFileSize(this.MAX_FILE_SIZE)})`);
    }

    // Check file type
    if (!this.ALLOWED_TYPES.includes(file.type)) {
      errors.push(`File type "${file.type}" is not allowed`);
    }

    // Check file name
    if (file.name.length > 255) {
      errors.push('File name is too long (maximum 255 characters)');
    }

    // Check for suspicious file extensions
    const suspiciousExtensions = ['.exe', '.bat', '.cmd', '.scr', '.pif', '.com'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (suspiciousExtensions.includes(fileExtension)) {
      errors.push('Executable files are not allowed');
    }

    // Large file warning
    if (file.size > 10 * 1024 * 1024) { // 10MB
      warnings.push('Large file may take longer to upload');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  // Validate multiple files
  static validateFiles(files: File[]): FileValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check total size
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > this.MAX_TOTAL_SIZE) {
      errors.push(`Total file size (${this.formatFileSize(totalSize)}) exceeds limit (${this.formatFileSize(this.MAX_TOTAL_SIZE)})`);
    }

    // Validate each file
    files.forEach((file, index) => {
      const validation = this.validateFile(file);
      validation.errors.forEach(error => errors.push(`File ${index + 1} (${file.name}): ${error}`));
      validation.warnings.forEach(warning => warnings.push(`File ${index + 1} (${file.name}): ${warning}`));
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  // Generate thumbnail for images
  private static async generateThumbnail(file: File, storagePath: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = async () => {
        // Calculate thumbnail dimensions (max 200x200)
        const maxSize = 200;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(async (blob) => {
          if (blob) {
            try {
              const thumbRef = ref(storage, storagePath);
              await uploadBytes(thumbRef, blob);
              const url = await getDownloadURL(thumbRef);
              resolve(url);
            } catch (error) {
              reject(error);
            }
          } else {
            reject(new Error('Failed to create thumbnail'));
          }
        }, 'image/jpeg', 0.7);
      };

      img.onerror = () => reject(new Error('Failed to load image for thumbnail'));
      img.src = URL.createObjectURL(file);
    });
  }

  // Start virus scan (placeholder - would integrate with actual scanning service)
  private static async startVirusScan(fileId: string, fileUrl: string): Promise<void> {
    try {
      // This would call an actual virus scanning service
      // For now, we'll just mark as clean after a delay
      setTimeout(async () => {
        await updateDoc(doc(db, 'files', fileId), {
          virusScanStatus: 'clean',
          updatedAt: serverTimestamp()
        });
      }, 5000);
    } catch (error) {
      console.error('Error during virus scan:', error);
      await updateDoc(doc(db, 'files', fileId), {
        virusScanStatus: 'error',
        updatedAt: serverTimestamp()
      });
    }
  }

  // Format file size
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Get file type icon
  static getFileTypeIcon(fileType: string): string {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.startsWith('video/')) return '🎥';
    if (fileType.startsWith('audio/')) return '🎵';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('sheet')) return '📊';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return '📊';
    if (fileType.includes('zip') || fileType.includes('rar')) return '🗜️';
    if (fileType.includes('text')) return '📄';
    return '📎';
  }

  // Check if file is image
  static isImage(fileType: string): boolean {
    return fileType.startsWith('image/');
  }

  // Check if file is video
  static isVideo(fileType: string): boolean {
    return fileType.startsWith('video/');
  }

  // Check if file is audio
  static isAudio(fileType: string): boolean {
    return fileType.startsWith('audio/');
  }

  // Get storage usage for school
  static async getStorageUsage(schoolId: string): Promise<{
    totalBytes: number;
    fileCount: number;
    byCategory: Record<string, { bytes: number; count: number }>;
  }> {
    try {
      const files = await this.getFiles(schoolId);
      
      const usage = {
        totalBytes: 0,
        fileCount: files.length,
        byCategory: {} as Record<string, { bytes: number; count: number }>
      };

      files.forEach(file => {
        usage.totalBytes += file.size;
        
        if (!usage.byCategory[file.category]) {
          usage.byCategory[file.category] = { bytes: 0, count: 0 };
        }
        
        usage.byCategory[file.category].bytes += file.size;
        usage.byCategory[file.category].count++;
      });

      return usage;
    } catch (error) {
      console.error('Error getting storage usage:', error);
      return { totalBytes: 0, fileCount: 0, byCategory: {} };
    }
  }

  // Clean up orphaned files
  static async cleanupOrphanedFiles(schoolId: string): Promise<number> {
    try {
      const files = await this.getFiles(schoolId);
      let cleanedCount = 0;

      for (const file of files) {
        if (file.parentId) {
          // Check if parent still exists
          let parentExists = false;
          
          switch (file.category) {
            case 'message':
              // Check if message exists
              const messageQuery = query(collection(db, 'messages'), where('id', '==', file.parentId));
              const messageSnapshot = await getDocs(messageQuery);
              parentExists = !messageSnapshot.empty;
              break;
              
            case 'task':
              // Check if task exists
              const taskQuery = query(collection(db, 'tasks'), where('id', '==', file.parentId));
              const taskSnapshot = await getDocs(taskQuery);
              parentExists = !taskSnapshot.empty;
              break;
          }

          if (!parentExists) {
            await this.deleteFile(file.id);
            cleanedCount++;
          }
        }
      }

      return cleanedCount;
    } catch (error) {
      console.error('Error cleaning up orphaned files:', error);
      return 0;
    }
  }
}

export default FileService;